import { useState } from 'react';
import { Link } from 'react-router';
import Sheet from '../../app/components/Sheet';
import { useApp } from '../../app/lib/AppContext';
import { vnDate } from '../../app/lib/coach';
import CoachPlanForm from './CoachPlanForm';

// The actions in a user page's header: plan (Pro until a date, unlimited, end the trial), coach seats, extra AI credits
// for today, their Góp ý conversation, lock / unlock. Each one asks first and (except credits) needs a reason; the server
// writes it to the audit log. `onDone` gets { header, audits } back; `onReload` reloads the whole page (seats).
const INPUT = 'input h-11 w-full rounded-[14px] border-line text-base';
const BUTTON = 'btn h-10 min-h-10 rounded-[12px] border-line bg-base-100 px-3 text-sm';
const tomorrowPlus = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

function ReasonField({ value, onChange, optional = false }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-semibold">Lý do{optional ? ' (không bắt buộc)' : ''}</span>
      <textarea className="textarea min-h-20 w-full rounded-[14px] border-line text-base" required={!optional} maxLength={250}
        placeholder="Ghi vào nhật ký quản trị" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

// One confirm step: the reason, any fields, and the button. Errors stay in the sheet.
function ActionForm({ children, label, danger = false, run, reasonOptional = false }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await run(reason.trim());
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      {children}
      <ReasonField value={reason} onChange={setReason} optional={reasonOptional} />
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <button type="submit" disabled={busy}
        className={`btn h-12 rounded-[14px] border-0 font-bold ${danger ? 'bg-error text-white' : 'btn-primary'}`}>
        {busy && <span className="loading loading-spinner loading-sm" />} {label}
      </button>
    </form>
  );
}

function PlanSheet({ h, call }) {
  const [until, setUntil] = useState(tomorrowPlus(30));
  const [mode, setMode] = useState(null);
  const adminPro = h.plan.admin_pro_until && new Date(h.plan.admin_pro_until) > new Date();
  const options = [
    ['pro', 'Tặng Pro đến một ngày'],
    adminPro && ['end_pro', `Kết thúc Pro quản trị tặng (đến ${vnDate(h.plan.admin_pro_until)})`],
    ['unlimited', h.plan.tier === 'unlimited' ? 'Bỏ không giới hạn' : 'Đặt không giới hạn'],
    h.trial_active && ['trial', 'Kết thúc dùng thử ngay'],
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Thao tác">
        {options.map(([id, label]) => (
          <button key={id} type="button" role="radio" aria-checked={mode === id} onClick={() => setMode(id)}
            className={`min-h-11 rounded-[14px] border px-3 text-left text-sm font-semibold ${mode === id ? 'border-[#17804f] bg-[#e3f1e9]' : 'border-line'}`}>{label}</button>
        ))}
      </div>
      {mode === 'pro' && (
        <ActionForm label="Tặng Pro" run={(reason) => call('post', 'pro', { until, reason })}>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-semibold">Pro đến hết ngày</span>
            <input type="date" className={`${INPUT} w-48`} min={tomorrowPlus(1)} value={until} onChange={(e) => setUntil(e.target.value)} required />
          </label>
          <p className="text-xs text-muted">Riêng với Pro đã mua: không rút ngắn hay thay gói người dùng đã trả tiền.</p>
        </ActionForm>
      )}
      {mode === 'end_pro' && <ActionForm label="Kết thúc Pro quản trị tặng" danger run={(reason) => call('del', 'pro', { reason })} />}
      {mode === 'unlimited' && (
        <ActionForm label={h.plan.tier === 'unlimited' ? 'Bỏ không giới hạn' : 'Đặt không giới hạn'}
          run={(reason) => call('put', 'unlimited', { value: h.plan.tier !== 'unlimited', reason })} />
      )}
      {mode === 'trial' && <ActionForm label="Kết thúc dùng thử" danger run={(reason) => call('del', 'trial', { reason })} />}
    </div>
  );
}

function CreditsSheet({ call }) {
  const [amount, setAmount] = useState(5);
  const [key] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now())));
  return (
    <ActionForm label={`Cộng ${amount || 0} lượt cho hôm nay`} reasonOptional
      run={(reason) => call('post', 'credits', { amount, reason }, { idempotencyKey: key })}>
      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Số lượt (1–50), chỉ trong hôm nay</span>
        <input type="number" min={1} max={50} className={`${INPUT} w-32`} value={amount} onChange={(e) => setAmount(e.target.value)} required />
      </label>
      <p className="text-xs text-muted">Hiện trong lịch sử lượt AI của người dùng là "Đội ngũ tặng lượt". Ngày mai lượt trở lại như bình thường.</p>
    </ActionForm>
  );
}

function LockSheet({ h, call }) {
  if (h.locked) {
    return (
      <ActionForm label="Mở khoá tài khoản" run={(reason) => call('del', 'lock', { reason })}>
        <p className="text-sm">Khoá lúc {vnDate(h.locked.at)}{h.locked.reason ? `: “${h.locked.reason}”` : ''}. Mở khoá thì người dùng đăng nhập lại được, link chia sẻ hoạt động lại.</p>
      </ActionForm>
    );
  }
  return (
    <ActionForm label="Khoá tài khoản" danger run={(reason) => call('post', 'lock', { reason })}>
      <p className="rounded-[14px] bg-[#fdebe4] p-3 text-sm text-[#8e3414]">
        Người dùng bị đăng xuất trên mọi thiết bị và không đăng nhập lại được; link chia sẻ ngừng hoạt động; nếu là HLV thì không mời
        hay tặng Pro mới được (quà đang chạy vẫn giữ đến hết hạn).
      </p>
    </ActionForm>
  );
}

export default function HeaderActions({ data, onDone, onReload }) {
  const { api } = useApp();
  const h = data.header;
  const [open, setOpen] = useState(null);
  const close = () => setOpen(null);
  const call = async (verb, action, body, options) => {
    const res = await api[verb](`/admin/users/${h.id}/${action}`, body, options);
    onDone(res);
    close();
  };
  const sheets = {
    plan: ['Tặng Pro / đổi gói', <PlanSheet key="plan" h={h} call={call} />],
    seats: [h.coach ? 'Chỗ HLV' : 'Cấp chỗ HLV', <CoachPlanForm key="seats" user={h} onSaved={() => { close(); onReload(); }} />],
    credits: ['Cộng lượt AI', <CreditsSheet key="credits" call={call} />],
    lock: [h.locked ? 'Mở khoá tài khoản' : 'Khoá tài khoản', <LockSheet key="lock" h={h} call={call} />],
  };
  const latest = data.feedback?.latest;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn h-10 min-h-10 rounded-[12px] border-0 bg-[#17804f] px-3 text-sm text-white" onClick={() => setOpen('plan')}>Tặng Pro / đổi gói</button>
        <button type="button" className={BUTTON} onClick={() => setOpen('seats')}>{h.coach ? 'Chỗ HLV' : 'Cấp chỗ HLV'}</button>
        <button type="button" className={BUTTON} onClick={() => setOpen('credits')}>Cộng lượt AI</button>
        {latest && <Link className={BUTTON} to={`/admin?thread=${latest.id}`}>Nhắn qua Góp ý</Link>}
        <button type="button" className={`${BUTTON} ${h.locked ? '' : 'border-error text-error'}`} onClick={() => setOpen('lock')}>
          {h.locked ? 'Mở khoá' : 'Khoá tài khoản'}
        </button>
      </div>
      {h.locked && (
        <p role="status" className="rounded-[14px] bg-[#fdebe4] p-3 text-sm text-[#8e3414]">
          Tài khoản đang bị khoá từ {vnDate(h.locked.at)}{h.locked.reason ? `: “${h.locked.reason}”` : ''}.
        </p>
      )}
      <Sheet open={!!open} onClose={close} title={open ? sheets[open][0] : ''}>
        {open && sheets[open][1]}
      </Sheet>
    </>
  );
}
